import { classifyCapgoReason } from './capgoReason';
import { describeUpdateFailure } from './failureDetail';
import type { UpdateRunnerDeps } from './updateDownload';

export const PROGRESSIVE_HINT_KEY = 'baobab_ota_progressive';

export const makeIsProgressiveTrigger =
  (isIosFn: () => boolean) =>
  (error: unknown): boolean =>
    isIosFn() && classifyCapgoReason(describeUpdateFailure(error)).reasonClass === 'timeout_wallclock';

interface CleanupPlugin {
  list(): Promise<{ bundles: Array<{ id: string; version: string; status: string }> }>;
  delete(options: { id: string }): Promise<void>;
}

export const makeCleanupFailedBundles =
  (plugin: CleanupPlugin) =>
  async (version: string): Promise<void> => {
    try {
      const { bundles } = await plugin.list();
      const ids = bundles.filter((b) => b.status === 'error' && b.version === version).map((b) => b.id);
      await Promise.allSettled(
        ids.map(async (id) => {
          await plugin.delete({ id });
        }),
      );
    } catch {
      // best effort: a leftover errored bundle only costs disk
    }
  };

type StoredHint = { version: string; until: number; nativeBuild?: number };

const parseHint = (raw: string | null): StoredHint | null => {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { version, until, nativeBuild } = parsed as Record<string, unknown>;
    if (typeof version !== 'string' || typeof until !== 'number' || !Number.isFinite(until)) return null;
    if (nativeBuild !== undefined && typeof nativeBuild !== 'number') return null;
    return { version, until, nativeBuild };
  } catch {
    return null;
  }
};

export const createProgressiveHintStore = (
  getStorage: () => Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
  getNativeBuild: () => number | undefined,
): {
  read: UpdateRunnerDeps['readProgressiveHint'];
  write: UpdateRunnerDeps['writeProgressiveHint'];
} => {
  const currentBuild = (): number | undefined => {
    try {
      return getNativeBuild();
    } catch {
      return undefined;
    }
  };

  return {
    read: () => {
      try {
        const storage = getStorage();
        const stored = parseHint(storage.getItem(PROGRESSIVE_HINT_KEY));
        if (!stored) return null;
        const current = currentBuild();
        if (current !== undefined && stored.nativeBuild !== undefined && stored.nativeBuild !== current) {
          storage.removeItem(PROGRESSIVE_HINT_KEY);
          return null;
        }
        return { version: stored.version, until: stored.until };
      } catch {
        return null;
      }
    },
    write: (hint) => {
      try {
        const storage = getStorage();
        if (hint === null) {
          storage.removeItem(PROGRESSIVE_HINT_KEY);
          return;
        }
        storage.setItem(PROGRESSIVE_HINT_KEY, JSON.stringify({ ...hint, nativeBuild: currentBuild() }));
      } catch {
        // best effort: a missing hint only costs one extra zip attempt
      }
    },
  };
};
